// LifeGuide guest-preview state — stored locally in the browser.
//
// A "guest" is anyone experiencing LifeGuide before creating an account.
// As of Feb 2026 the guest experience is a full 3-DAY PRO TRIAL, not a
// per-interaction cap. The guest keeps unlimited access to Guidance,
// Coach, Say It Better, Journal (scratch), and Saved Wisdom (scratch)
// until either they sign up (recommended, so their work carries across
// devices) or the 72-hour trial window ends.
//
// We NEVER store PII locally. Only:
//   - a stable random id (per-browser continuity)
//   - trial_started_at / trial_expires_at
//   - short in-browser conversation history for Coach + Guidance context
//   - scratch Journal + Saved Wisdom (merged into the real account on signup)
//   - Proverb hand-off context (topic, from_proverb) if the caller passed it

const KEY = "lg_guest";
export const GUEST_TRIAL_DAYS = 3;
export const GUEST_TRIAL_MS = GUEST_TRIAL_DAYS * 24 * 60 * 60 * 1000;

// Legacy export kept so any lingering import doesn't crash — no longer used
// to gate anything.
export const GUEST_INTERACTION_LIMIT = 0;

function _uid() {
  return (
    "g_" +
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36)
  );
}

function _iso(t) {
  return new Date(t).toISOString();
}

/**
 * Return the raw guest state or null. Read-only helper — callers that want
 * to create the guest on first visit should call `ensureGuest()`.
 */
export function getGuest() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Return the exact millisecond the current guest's trial ends. Null if
 * there is no guest yet.
 */
export function trialExpiresAt() {
  const g = getGuest();
  if (!g?.trial_expires_at) return null;
  const t = Date.parse(g.trial_expires_at);
  return Number.isFinite(t) ? t : null;
}

export function trialMsRemaining() {
  const end = trialExpiresAt();
  if (!end) return 0;
  return Math.max(0, end - Date.now());
}

export function isTrialActive() {
  return trialMsRemaining() > 0;
}

export function trialDaysLeft() {
  const ms = trialMsRemaining();
  if (ms <= 0) return 0;
  // Round UP so "23 hours left" reads as "1 day left" — kinder UX than
  // truncating to 0.
  return Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function trialHoursLeft() {
  const ms = trialMsRemaining();
  if (ms <= 0) return 0;
  return Math.max(1, Math.ceil(ms / (60 * 60 * 1000)));
}

/**
 * Create the guest on first visit or hydrate an existing one.
 * On first creation, starts the 3-day Pro trial clock. Preserves the
 * trial window on subsequent hydrations so refreshing the page never
 * extends or resets the trial.
 */
export function ensureGuest(seed = {}) {
  const existing = getGuest();
  if (existing) {
    const merged = {
      exchanges: [],
      saved: [],
      journal_scratch: [],
      wisdom_scratch: [],
      from_proverb: false,
      proverb_topic: null,
      ...existing,
      ...seed,
    };
    // Backfill trial timestamps for any guest state that predates the
    // time-based trial (i.e. older localStorage from the interaction-cap
    // model). Grant them the full 3 days from now — this only fires once
    // per browser.
    if (!merged.trial_started_at || !merged.trial_expires_at) {
      const now = Date.now();
      merged.trial_started_at = _iso(now);
      merged.trial_expires_at = _iso(now + GUEST_TRIAL_MS);
    }
    localStorage.setItem(KEY, JSON.stringify(merged));
    return merged;
  }
  const now = Date.now();
  const created = {
    id: _uid(),
    created_at: _iso(now),
    trial_started_at: _iso(now),
    trial_expires_at: _iso(now + GUEST_TRIAL_MS),
    exchanges: [],
    saved: [],
    journal_scratch: [],
    wisdom_scratch: [],
    from_proverb: false,
    proverb_topic: null,
    ...seed,
  };
  localStorage.setItem(KEY, JSON.stringify(created));
  return created;
}

export function updateGuest(patch) {
  const cur = getGuest();
  if (!cur) return null;
  const next = { ...cur, ...patch };
  localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function appendExchange({ role, kind, content }) {
  const cur = getGuest();
  if (!cur) return null;
  const exchanges = Array.isArray(cur.exchanges) ? cur.exchanges : [];
  // Cap to last 60 turns so localStorage stays bounded even on a chatty day.
  const trimmed = [
    ...exchanges,
    { role, kind, content, at: new Date().toISOString() },
  ].slice(-60);
  return updateGuest({ exchanges: trimmed });
}

/**
 * Recent turns for a specific kind ("guidance" | "coach"), oldest-first,
 * capped at `limit` so we don't blow up the LLM prompt.
 */
export function recentTurns(kind, limit = 12) {
  const cur = getGuest();
  if (!cur) return [];
  return (cur.exchanges || [])
    .filter((e) => e.kind === kind)
    .map((e) => ({ role: e.role, content: e.content }))
    .slice(-limit);
}

// --- Journal scratch ------------------------------------------------------
// Guest journal entries live in localStorage until the guest signs up. On
// signup, /auth/signup returns a token and the client merges these entries
// into the real journal via /journals POST. See AuthContext.signup.

export function listJournalScratch() {
  const cur = getGuest();
  return Array.isArray(cur?.journal_scratch) ? cur.journal_scratch : [];
}

export function addJournalScratch(entry) {
  const cur = getGuest();
  if (!cur) return null;
  const list = Array.isArray(cur.journal_scratch) ? cur.journal_scratch : [];
  const row = {
    id: "js_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36),
    title: (entry.title || "").slice(0, 240),
    content: (entry.content || "").slice(0, 10000),
    created_at: new Date().toISOString(),
  };
  return updateGuest({ journal_scratch: [row, ...list].slice(0, 50) });
}

export function removeJournalScratch(id) {
  const cur = getGuest();
  if (!cur) return null;
  const list = (cur.journal_scratch || []).filter((r) => r.id !== id);
  return updateGuest({ journal_scratch: list });
}

// --- Saved Wisdom scratch -------------------------------------------------

export function listWisdomScratch() {
  const cur = getGuest();
  return Array.isArray(cur?.wisdom_scratch) ? cur.wisdom_scratch : [];
}

export function addWisdomScratch(entry) {
  const cur = getGuest();
  if (!cur) return null;
  const list = Array.isArray(cur.wisdom_scratch) ? cur.wisdom_scratch : [];
  const row = {
    id: "ws_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36),
    title: (entry.title || "").slice(0, 240),
    body: (entry.body || "").slice(0, 4000),
    reference: entry.reference || null,
    created_at: new Date().toISOString(),
  };
  return updateGuest({ wisdom_scratch: [row, ...list].slice(0, 100) });
}

export function removeWisdomScratch(id) {
  const cur = getGuest();
  if (!cur) return null;
  const list = (cur.wisdom_scratch || []).filter((r) => r.id !== id);
  return updateGuest({ wisdom_scratch: list });
}

export function clearGuest() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
